<template>
  <!-- Total price display in footer for mobile -->
  <TotalPriceDisplay
    v-if="
      configuratorStore.isUploadConfigurator &&
      !configuratorStore.hasMediaclipError
    "
    :class="
      configuratorStore.isSizeSelectorOpen
        ? 'translate-y-full opacity-0'
        : 'translate-y-0'
    "
    class="absolute inset-x-0 -top-10 -z-10 mb-3 px-4 transition-all duration-300"
  />

  <div
    :class="{ 'mt-9': !configuratorStore.isSizeSelectorOpen }"
    class="z-50 flex h-14 w-full justify-between gap-x-4 bg-white transition-all duration-300"
  >
    <template v-if="configuratorStore.isSizeSelectorOpen">
      <!-- Previous button -->
      <BackButton
        :class="{
          'disabled:cursor-default': configuratorStore.hasMediaclipError,
        }"
        :disabled="
          configuratorStore.isUpdatingProject ||
          configuratorStore.hasMediaclipError ||
          configuratorStore.buttonsOnCooldown
        "
        class="w-1/5"
        @click="() => configuratorStore.closeSizeSelector(false)"
      />

      <!-- Save button when size selector is open on mobile -->
      <DefaultButton
        class="h-full w-4/5"
        @click="() => configuratorStore.closeSizeSelector(true)"
      >
        <template #default>
          {{ $t('pages.navigation.footer.buttons.save') }}
        </template>

        <template #icon>
          <CheckIcon class="not-sr-only h-full stroke-[2.5px] py-1" />
        </template>
      </DefaultButton>
    </template>

    <template v-else>
      <!-- Previous button -->
      <BackButton
        :class="{
          'disabled:cursor-default': configuratorStore.hasMediaclipError,
        }"
        :disabled="
          configuratorStore.isUpdatingProject ||
          configuratorStore.hasMediaclipError ||
          configuratorStore.buttonsOnCooldown
        "
        class="w-1/5"
        @click="configuratorStore.previousStep"
      />

      <template v-if="configuratorStore.showNextButton">
        <!-- Next button (no extra options -->
        <AddToCartButton
          v-if="extraOptionsStore.productOptionsCategories.length === 0"
          :disabled="configuratorStore.disableNextButton"
          :loading="configuratorStore.isUpdatingProject"
          @click="configuratorStore.nextStep(false)"
        />

        <!-- Next button -->
        <DefaultTooltip
          v-else
          :disabled="popperDisabled"
          :content="
            configuratorStore.isLetterCollageConfigurator ||
            (configuratorStore.isSimpleConfigurator &&
              mediaclipStore.amountOfWorksheets === 0)
              ? $t('pages.configurator.buttons.next.tooltip.upload_remaining')
              : mediaclipStore.emptyWorksheets.length > 0
                ? $tc(
                    'pages.configurator.worksheet_container.buttons.next.tooltip.selects_remaining',
                    {
                      count: mediaclipStore.emptyWorksheets.length,
                    }
                  )
                : $tc(
                    'pages.configurator.worksheet_container.buttons.next.tooltip.checks_remaining',
                    {
                      count:
                        mediaclipStore.amountOfWorksheets -
                        mediaclipStore.filledWorksheets.length,
                    }
                  )
          "
          class="h-full w-full"
        >
          <DefaultButton
            :disabled="
              configuratorStore.hasMediaclipError ||
              configuratorStore.buttonsOnCooldown ||
              configuratorStore.disableNextButton
            "
            :loading="configuratorStore.isUpdatingProject"
            :show-loading-icon="false"
            class="h-full w-full text-xl"
            @click="configuratorStore.nextStep(true)"
          >
            <template
              v-if="
                !configuratorStore.hasSavedSizeSelection &&
                configuratorStore.isUploadConfigurator
              "
            >
              {{ $t('pages.navigation.footer.buttons.choose_size') }}
            </template>

            <template v-else>
              {{ $t('pages.navigation.footer.buttons.next') }}
            </template>
          </DefaultButton>
        </DefaultTooltip>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import BackButton from '@/components/Buttons/BackButton.vue';
import TotalPriceDisplay from '@/pages/ProductPage/partials/Configurator/ConfiguratorLayout/ConfiguratorFooter/TotalPriceDisplay.vue';
import { useConfiguratorStore } from '@/stores/Configurator/ConfiguratorStore';
import { useMediaclipStore } from '@/stores/Configurator/MediaclipStore';
import { useExtraOptionsStore } from '@/stores/ExtraOptionsStore';
import { CheckIcon } from '@heroicons/vue/24/outline';
import AddToCartButton from '../../AddToCartButton.vue';
import DefaultButton from '@/components/Buttons/DefaultButton.vue';
import DefaultTooltip from '@/components/Tooltips/DefaultTooltip.vue';
import { computed } from 'vue';

const extraOptionsStore = useExtraOptionsStore();
const configuratorStore = useConfiguratorStore();
const mediaclipStore = useMediaclipStore();

const popperDisabled = computed(
  () =>
    configuratorStore.isUploadConfigurator ||
    (!configuratorStore.isLetterCollageConfigurator &&
      !configuratorStore.isSimpleConfigurator) ||
    (configuratorStore.allPicturesUploaded &&
      (mediaclipStore.filledWorksheets.length ===
        mediaclipStore.amountOfWorksheets ||
        mediaclipStore.filledWorksheets.length ===
          mediaclipStore.amountOfWorksheets ||
        mediaclipStore.amountOfWorksheets === 0))
);
</script>
